import * as Reflux from "reflux";
import _ from "lodash";

import { SessionStore } from "../uidispatcherapi/SessionStore";
import URLUtil from "../utils/UrlUtil";
import RequestUtil from "../utils/RequestUtil";
import CountryUtil from "../uidispatcherapi/CountryUtil";
import { trackBannerError } from "../utils/BannerTrackingUtil";

import { PageType, BannerType, TargetParams } from "../utils/BannerContainer";
import CustomerStore from "./CustomerStore";
import FeatureUtil from "../utils/FeatureUtil";

type AbstractBanner = { type: BannerType };
type InvalidBanner = AbstractBanner & { isInvalid: boolean; reason: string };
export type EmptyBanner = AbstractBanner & { isEmpty: boolean };
export type BannerGroup = Array<Banner>;
type ResponseType = Banner | InvalidBanner;
export type Alignment = "left" | "center" | "right";

const EMPTY_BANNER_GROUP: BannerGroup = [];

export type TimeSlot = {
    from_timestamp?: number; // eslint-disable-line camelcase
    until_timestampy?: number; // eslint-disable-line camelcase
};

export type ConfiguredTargetParams = {
    customerTargetGroups?: Array<string>;
    customerCategoryGroups?: Array<string>;
    orderType?: string;
    customerTypes?: Array<string>;
    stores?: Array<string>;
    time_slots?: Array<TimeSlot>; // eslint-disable-line camelcase
    capping?: { session?: number };
};

export type Article = {
    articleNumber: string;
    bettyBundleId: string;
    bettyVariantId: string;
    bundleNumber: string;
    variantNumber: string;
};

export type ArticlesTeaser = {
    articleId: Article;
    text?: string;
    link: {
        text: string;
        url: string;
    };
};

export type Image = {
    desktop: {
        imageUrl: string;
        width: number;
        height: number;
    };
    tablet?: {
        imageUrl: string;
        width: number;
        height: number;
    };
    mobile?: {
        imageUrl: string;
        width: number;
        height: number;
    };
};

export type BannerRequest = {
    pageType: PageType;
    bannerType: BannerType;
    targetParams?: TargetParams;
};

export const BannerStoreActions = {
    LoadBannerAction: Reflux.createAction({
        asyncResult: true
    }),
    DisposeBannerAction: Reflux.createAction({
        asyncResult: false
    })
};

export type Banner = AbstractBanner & {
    images: Array<Image>;
    link?: string;
    linkType?: string;
    text: string;
    page: PageType;
    isI18n?: boolean;
    textAlignment: Alignment;
    placementId?: string;
    placementName?: string;
    campaignId?: string;
    campaignName?: string;
    creativeId?: string;
    advertisement?: boolean;
    articleData?: Array<Object>;
    articleSponsoredDesc?: Array<ArticlesTeaser>;
    orderType?: string;
    configuredTargetParams?: ConfiguredTargetParams;
};

let bannerConfig: Map<string, BannerGroup>;

export function isValidBanner(bannerSpec: ResponseType) {
    if ((bannerSpec as InvalidBanner).isInvalid !== undefined) {
        //TODO: log the issue maybe?
        return !(bannerSpec as InvalidBanner).isInvalid;
    }
    return true;
}

function placementFromJson(jsonPlacement: any): ResponseType {
    if (
        jsonPlacement.type !== "articlesTilesTeaser" &&
        (jsonPlacement.images === undefined || jsonPlacement.images.length === 0)
    ) {
        return {
            type: jsonPlacement.type as BannerType,
            isInvalid: true,
            reason: `Images should be present for ${jsonPlacement.type}`
        };
    }
    return {
        placementId: jsonPlacement.placementId,
        placementName: jsonPlacement.placementName,
        campaignId: jsonPlacement.campaignId,
        campaignName: jsonPlacement.campaignName,
        creativeId: jsonPlacement.creativeId,
        images: jsonPlacement.images,
        link: jsonPlacement.link,
        linkType: _.get(jsonPlacement, ["target", "type"]),
        text: jsonPlacement.text,
        type: jsonPlacement.type,
        page: jsonPlacement.page,
        isI18n: jsonPlacement.isI18n,
        textAlignment: jsonPlacement.textAlignment,
        advertisement: jsonPlacement.advertisement,
        articleSponsoredDesc: jsonPlacement.articleSponsoredDesc,
        articleData: jsonPlacement.articleData,
        orderType: jsonPlacement.orderType
    };
}

function getBannerId(bannerRequest: BannerRequest) {
    return `${bannerRequest.pageType}-${bannerRequest.bannerType} `;
}

const BannerStore = Reflux.createStore({
    init() {
        bannerConfig = new Map();
        this.listenTo(CustomerStore, this.onCustomerStoreTrigger);
        this.listenTo(BannerStoreActions.DisposeBannerAction, this.disposeBanner);
        this.listenTo(BannerStoreActions.LoadBannerAction, this.loadOneBanner);
    },

    onCustomerStoreTrigger() {
        this.trigger();
    },

    getBannerGroup(bannerRequest: BannerRequest): BannerGroup {
        const bannerId = getBannerId(bannerRequest);
        if (bannerConfig.has(bannerId)) {
            return bannerConfig.get(bannerId) as BannerGroup;
        } else {
            return EMPTY_BANNER_GROUP;
        }
    },

    disposeBanner(bannerRequest: BannerRequest) {
        bannerConfig.delete(getBannerId(bannerRequest));
    },

    loadOneBanner(bannerRequest: BannerRequest) {
        const country = CountryUtil.getCountry();
        const bannerId = getBannerId(bannerRequest);
        const doLoadBannersCompleted = (request: any) => {
            bannerConfig = bannerConfig.set(bannerId, this.parseResponse(request));
            this.trigger();
        };
        const doLoadBannersFailed = (err: any) => {
            bannerConfig = bannerConfig.set(bannerId, EMPTY_BANNER_GROUP);
            if (err.status !== 404) {
                trackBannerError(bannerRequest.pageType, bannerRequest.bannerType, bannerRequest.targetParams);
            }

            this.trigger();
        };

        const params = _.assign({}, bannerRequest.targetParams || {}, {
            sessionId: SessionStore.getClientSessionId(),
            stores: SessionStore.getCustomerStore()
        });

        if (FeatureUtil.isOrderHabitsFeatureEnabled()) {
            _.assign(params, { customerId: SessionStore.getCustomerId() });
        }

        this.sanitizeQueryParams(params);
        const query = RequestUtil.buildQuery(params);
        const url =
            "/cia/banner/country/" +
            country +
            "/page/" +
            bannerRequest.pageType +
            "/type/" +
            bannerRequest.bannerType +
            (query ? "?" + query : "");
        URLUtil.manualXMLHttpRequest(url, "GET", null, doLoadBannersCompleted, doLoadBannersFailed, 30000, null);
    },

    sanitizeQueryParams(params: { [x: string]: any }): { [x: string]: any } | null | undefined {
        // Replaces spaces with backslash space for exact query matching in case query value had spaces
        return Object.keys(params).map(key => {
            const queryParamVal = params[key];
            if (!queryParamVal) {
                return null;
            }
            if (Array.isArray(queryParamVal)) {
                params[key] = queryParamVal.map(val => {
                    return val.replace(/\s/g, "\\ ");
                });
            } else {
                // String value
                params[key] = queryParamVal.replace(/\s/g, "\\ ");
            }
            return params;
        });
    },

    parseResponse(request: { [x: string]: any }): BannerGroup {
        const response = JSON.parse(request.response);
        if (Array.isArray(response)) {
            return response
                .map(placementFromJson)
                .filter(isValidBanner)
                .map(okBanner => okBanner as Banner);
        } else {
            const banner = placementFromJson(response);
            if (isValidBanner(banner)) {
                return [banner as Banner];
            } else {
                return EMPTY_BANNER_GROUP;
            }
        }
    }
});

export default BannerStore;
